import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Copy, ExternalLink, PackageOpen, Receipt, Users } from "lucide-react";
import { useState } from "react";
import {
  STORE_CURRENCY,
  contentFor,
  formatPrice,
  storePath,
  type DownlineMember,
  type Order,
  type StoreWithItems,
} from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { ProductThumb } from "@/features/admin/products/components/ProductThumb";
import { OrderStatusBadge } from "@/features/admin/orders/OrderStatusBadge";
import { formatOrderDate } from "@/features/admin/orders/format";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Las cards del dashboard del franquiciatario (Anahí, 27-ago): "sus
 * productos actuales, sus clientes o su red, sus ventas". CADA UNA es
 * un componente — se componen aquí, en el resumen del socio, o donde
 * haga falta. Ningún número se inventa: leen los mismos endpoints que
 * sus secciones.
 * ═══════════════════════════════════════════════════════════════════
 */

/** El marco común: card blanca, título y su puerta a la sección. */
export function SectionCard({
  title,
  to,
  toLabel,
  children,
}: {
  title: string;
  to?: string;
  toLabel?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-semibold text-ds-ink">{title}</h2>
        {to && (
          <Button as={Link} to={to} variant="ghost" size="sm" rightIcon={<ArrowRight />}>
            {toLabel ?? t("Ver todo")}
          </Button>
        )}
      </div>
      {children}
    </section>
  );
}

/** La tienda: el enlace listo para compartir, todos los días. */
export function StoreLinkCard({ store }: { store: StoreWithItems | undefined }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const slug = store?.store.slug;
  const url = slug ? `${window.location.origin}${storePath(slug)}` : null;

  return (
    <SectionCard title={t("Tu tienda")} to="/mi-negocio/tienda" toLabel={t("Configurar")}>
      {store ? (
        <div className="grid gap-2">
          <p className="text-[20px] font-semibold text-ds-ink">{store.store.name}</p>
          {url && <p className="break-all font-mono text-[12px] text-ds-ink-3">{url}</p>}
          <div className="mt-1 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={copied ? <Check /> : <Copy />}
              onClick={() => {
                if (!url) return;
                void navigator.clipboard?.writeText(url);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? t("Copiado") : t("Copiar enlace")}
            </Button>
            {slug && (
              <Button size="sm" as={Link} to={storePath(slug)} leftIcon={<ExternalLink />}>
                {t("Ver mi tienda")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-ds-ink-3">{t("Tu tienda se abre en la sección Mi tienda.")}</p>
      )}
    </SectionCard>
  );
}

/** Los productos ACTUALES: lo que la tienda vende hoy, con su stock. */
export function ProductsCard({ store, max = 5 }: { store: StoreWithItems | undefined; max?: number }) {
  const { t } = useTranslation();
  const items = (store?.items ?? []).filter((item) => item.enabled).slice(0, max);

  return (
    <SectionCard title={t("Tus productos")} to="/mi-negocio/tienda" toLabel={t("Ver todos")}>
      {items.length === 0 ? (
        <EmptyRow
          icon={<PackageOpen className="h-4 w-4" />}
          text={t("Tu inventario sale de tus paquetes mayoristas. Adquiere uno para llenar tu tienda.")}
        />
      ) : (
        <ul className="grid gap-2">
          {items.map(({ product, stock, priceCents }) => {
            const content = contentFor(product.translations, "es");
            return (
              <li
                key={product.id}
                className="flex items-center gap-3 rounded-2xl border border-ds-border px-3 py-2"
              >
                <ProductThumb url={product.imageUrl} name={content.name} size="sm" />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ds-ink">
                  {content.name}
                </span>
                <span className="font-mono text-[13px] text-ds-ink">
                  {typeof priceCents === "number" ? formatPrice(priceCents, STORE_CURRENCY) : "—"}
                </span>
                <span
                  className={`rounded-ds-pill px-2 py-0.5 text-[11px] font-semibold ${
                    stock > 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                  }`}
                >
                  {stock > 0 ? t("{{n}} en stock", { n: stock }) : t("Agotado")}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

/** Las ventas recientes de la tienda personalizada. */
export function SalesCard({ orders }: { orders: Order[] }) {
  const { t } = useTranslation();

  return (
    <SectionCard title={t("Ventas recientes")} to="/mi-negocio/ordenes" toLabel={t("Ver todas")}>
      {orders.length === 0 ? (
        <EmptyRow
          icon={<Receipt className="h-4 w-4" />}
          text={t("Cada pedido hecho en tu tienda personalizada aparecerá aquí.")}
        />
      ) : (
        <ul className="grid gap-2">
          {orders.map((order) => (
            <li
              key={order.id}
              className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border px-3.5 py-2.5"
            >
              <span className="font-mono text-[12.5px] text-ds-ink-3">{order.folio}</span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ds-ink">
                {order.customer.name}
              </span>
              <span className="font-mono text-[13px] font-semibold text-ds-ink">
                {formatPrice(order.totalCents, order.currency)}
              </span>
              <OrderStatusBadge status={order.status} />
              <span className="text-[12px] text-ds-ink-3">{formatOrderDate(order.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/** La red: los invitados más recientes, con su nivel. */
export function NetworkCard({
  members,
  total,
  basePath,
  max = 5,
}: {
  members: DownlineMember[];
  total: number;
  /** "/mi-negocio" o "/socio": la card vive en los dos paneles. */
  basePath: string;
  max?: number;
}) {
  const { t } = useTranslation();
  const recent = members.slice(0, max);

  return (
    <SectionCard
      title={total > 0 ? t("Tu red ({{n}})", { n: total }) : t("Tu red")}
      to={`${basePath}/red`}
      toLabel={t("Ver la red")}
    >
      {recent.length === 0 ? (
        <EmptyRow
          icon={<Users className="h-4 w-4" />}
          text={t("Comparte tu enlace de invitación: quien se registre con él aparecerá aquí.")}
        />
      ) : (
        <ul className="grid gap-2">
          {recent.map((member) => (
            <li
              key={member.userId}
              className="flex items-center gap-3 rounded-2xl border border-ds-border px-3 py-2"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-lavender-bg-2 text-[12px] font-bold text-ds-violet-pop">
                {member.displayName
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((word) => word[0])
                  .join("")
                  .toUpperCase()}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-ds-ink">
                {member.displayName}
              </span>
              <span className="text-[12px] text-ds-ink-3">{t("Nivel {{n}}", { n: member.level })}</span>
              <span className="text-[12px] text-ds-ink-3">{formatOrderDate(member.enrolledAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function EmptyRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-2xl bg-ds-mist/40 px-4 py-3 text-ds-ink-3">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <p className="text-[13px] leading-relaxed">{text}</p>
    </div>
  );
}
